import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Search as SearchIcon, SlidersHorizontal, X, ChevronDown, Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchChangelog, fetchEntities } from "../lib/dataFetchers";
import { ChangelogTimeline } from "../components/ChangelogTimeline";
import { HistoryTimelineErrorBoundary } from "../components/HistoryTimelineErrorBoundary";
import { groupByDay, classifyAction, isSystemRow } from "../lib/changelogFormat";
import type { ActionType } from "../lib/changelogFormat";
import { useDebounced } from "../lib/useDebounced";
import { Button } from "../components/ui/Button";
import { SectionCard } from "../components/ui/SectionCard";
import { cn } from "../lib/cn";
import { getFieldLabel } from "../lib/types";
import type { ChangeLog } from "../lib/types";

// Date preset helpers
function datePreset(days: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

const ACTION_TYPES: ActionType[] = [
  "created", "created-entity", "updated", "deleted", "renewed", "reassigned", "exported", "confirmed",
];

export default function History(): JSX.Element {
  const { t, i18n } = useTranslation(["audit", "common"]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(50);

  // Active filters
  const [filterEntityId, setFilterEntityId] = useState<string>("");
  const [filterField, setFilterField] = useState<string>("");
  const [filterAction, setFilterAction] = useState<ActionType | "">("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");

  // UI toggles
  const [groupByEntity, setGroupByEntity] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [entityPickerOpen, setEntityPickerOpen] = useState(false);
  const [actionPickerOpen, setActionPickerOpen] = useState(false);
  const [datepickerOpen, setDatepickerOpen] = useState(false);
  const [entitySearch, setEntitySearch] = useState("");

  // entityId deep-link
  const entityIdParam = searchParams.get("entityId") ?? "";
  useEffect(() => {
    if (entityIdParam && filterEntityId !== entityIdParam) {
      setFilterEntityId(entityIdParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityIdParam]);

  // field deep-link — from FieldEditHistory "See full history →" links
  const fieldParam = searchParams.get("field") ?? "";
  useEffect(() => {
    if (fieldParam && filterField !== fieldParam) {
      setFilterField(fieldParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldParam]);

  const debouncedQ = useDebounced(q, 300);

  // Reset pagination when filters change
  useEffect(() => { setLimit(50); }, [debouncedQ, filterEntityId, filterField, filterFromDate, filterToDate]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "changelog",
      { limit, q: debouncedQ, entityId: filterEntityId, field: filterField, from: filterFromDate, to: filterToDate, hideSystem: true },
    ],
    queryFn: () =>
      fetchChangelog({
        limit,
        q: debouncedQ || undefined,
        entityId: filterEntityId || undefined,
        field: filterField || undefined,
        from: filterFromDate || undefined,
        to: filterToDate || undefined,
        hideSystem: true,
      }),
    // "Load more" bumps `limit`, which changes the query key. Without this the
    // list would blank to the loading skeleton on every click (data → undefined
    // for the new key) and re-render from the top, so newly loaded older rows
    // landed below the fold and it looked like nothing happened. Keeping the
    // previous page's data means the list stays put and grows in place.
    placeholderData: keepPreviousData,
  });

  // Entities for entity picker
  const { data: entitiesData } = useQuery({
    queryKey: ["entities", { limit: 200 }],
    queryFn: () => fetchEntities({ limit: 200 }),
  });
  const allEntities = useMemo(() => entitiesData?.entities ?? [], [entitiesData]);

  const filteredEntityOptions = useMemo(() => {
    if (!entitySearch) return allEntities.slice(0, 20);
    const sq = entitySearch.toLowerCase();
    return allEntities.filter((e) => e.name.toLowerCase().includes(sq)).slice(0, 20);
  }, [allEntities, entitySearch]);

  const selectedEntity = useMemo(
    () => allEntities.find((e) => e.id === filterEntityId),
    [allEntities, filterEntityId],
  );

  const rawItems = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? rawItems.length;
  const hasMore = rawItems.length < total;

  // Quick-filter pill counts (based on rawItems, before client-side action filter)
  const quickFilterCounts = useMemo(() => ({
    all: rawItems.length,
    updated: rawItems.filter((i) => classifyAction(i.field).type === "updated").length,
    created: rawItems.filter((i) => {
      const t = classifyAction(i.field).type;
      return t === "created" || t === "created-entity";
    }).length,
    deleted: rawItems.filter((i) => classifyAction(i.field).type === "deleted").length,
    renewed: rawItems.filter((i) => classifyAction(i.field).type === "renewed").length,
  }), [rawItems]);

  // Client-side filters (Action)
  const items = useMemo(() => {
    let result = rawItems;
    // Action filter
    if (filterAction) {
      result = result.filter((c) => {
        const t = classifyAction(c.field).type;
        if (filterAction === "created") return t === "created" || t === "created-entity";
        return t === filterAction;
      });
    }
    return result;
  }, [rawItems, filterAction]);

  // Day grouping
  const grouped = useMemo(
    () => groupByDay(items, i18n.language),
    [items, i18n.language],
  );

  // Defence-in-depth: if any system rows slip through server-side filtering,
  // skip day-group headers whose items are ALL system rows (empty after FE filter).
  const visibleGroups = useMemo(
    () => grouped.filter((g) => g.items.some((item) => !isSystemRow(item))),
    [grouped],
  );

  // Group by entity
  const groupedByEntity = useMemo(() => {
    if (!groupByEntity) return null;
    const map = new Map<string, ChangeLog[]>();
    for (const item of items) {
      const key = item.entity?.name ?? item.targetName ?? t("audit:unknown_entity", { defaultValue: "Unknown" });
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items, groupByEntity, t]);

  const hasActiveFilter =
    Boolean(debouncedQ) ||
    Boolean(filterEntityId) ||
    Boolean(filterField) ||
    Boolean(filterFromDate) ||
    Boolean(filterToDate) ||
    Boolean(filterAction);

  const activeFilterCount = [
    Boolean(filterEntityId),
    Boolean(filterField),
    Boolean(filterFromDate || filterToDate),
    Boolean(filterAction),
  ].filter(Boolean).length;

  const clearAllFilters = () => {
    setFilterEntityId("");
    setFilterField("");
    setFilterAction("");
    setFilterFromDate("");
    setFilterToDate("");
    setQ("");
    setSearchParams({}, { replace: true });
  };

  const isNoMatch = !isLoading && items.length === 0 && hasActiveFilter;
  const isEmpty = !isLoading && items.length === 0 && !hasActiveFilter;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Page header */}
      <div className="flex items-end justify-between gap-3 flex-wrap mb-[18px]">
        <div>
          <h1 className="text-[24px] leading-[1.1] font-display font-semibold text-text">
            {t("audit:page_title")}
          </h1>
          <p className="text-[13px] text-muted mt-[5px]">{t("audit:page_subtitle")}</p>
        </div>
        {/* Toggles */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setGroupByEntity((v) => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-3 h-9 text-[12.5px] font-medium transition-colors min-h-[44px] sm:min-h-[36px]",
              groupByEntity
                ? "bg-accent text-white border-accent"
                : "bg-surface border-border text-muted hover:bg-surface-2",
            )}
          >
            <Layers className="h-3.5 w-3.5" aria-hidden />
            {t("audit:group_by_entity", { defaultValue: "Group by entity" })}
          </button>
        </div>
      </div>

      {/* Search + filter bar */}
      <div className="bg-surface border border-border rounded-[11px] shadow-card p-3 mb-3 space-y-3">
        {/* Search row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-[11px] top-1/2 -translate-y-1/2 h-4 w-4 text-dim" aria-hidden />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              type="search"
              placeholder={t("common:search_placeholder_audit")}
              aria-label={t("audit:page_title")}
              className="w-full h-9 pl-[35px] pr-3 border border-border rounded-lg bg-surface-2 text-[13.5px] text-text focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none"
            />
          </div>
          {/* Mobile filters toggle */}
          <button
            type="button"
            className={cn(
              "md:hidden inline-flex items-center gap-1.5 h-9 rounded-lg border px-3 text-[12.5px] font-medium transition-colors min-h-[44px] sm:min-h-[36px]",
              mobileFiltersOpen ? "bg-accent text-white border-accent" : "bg-surface border-border text-muted",
            )}
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
            {t("audit:filters_button", { defaultValue: "Filters" })}
            {activeFilterCount > 0 && (
              <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-white text-accent text-[9px] font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* Filter chips row — hidden on mobile unless toggled */}
        <div className={cn("flex flex-wrap gap-2", !mobileFiltersOpen && "hidden md:flex")}>
          {/* Entity filter chip */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setEntityPickerOpen((v) => !v)}
              className={cn(
                "inline-flex items-center gap-1.5 h-8 rounded-full border px-3 text-[12.5px] font-medium transition-colors",
                filterEntityId ? "bg-accent/10 border-accent/30 text-accent" : "bg-surface-2 border-border text-muted hover:bg-surface",
              )}
            >
              {selectedEntity ? (
                <>{selectedEntity.country?.flag ?? ""} {selectedEntity.name}</>
              ) : t("audit:filter_entity", { defaultValue: "Entity" })}
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
            {filterEntityId && (
              <button
                type="button"
                onClick={() => { setFilterEntityId(""); setSearchParams({}, { replace: true }); }}
                aria-label={t("audit:filter_remove_entity")}
                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/80"
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            )}
            {entityPickerOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 w-[280px] bg-surface border border-border rounded-[10px] shadow-pop p-2">
                <input
                  type="search"
                  placeholder={t("common:search")}
                  value={entitySearch}
                  onChange={(e) => setEntitySearch(e.target.value)}
                  className="w-full h-8 px-2 rounded-lg border border-border bg-surface-2 text-[13px] focus:outline-none focus:border-accent mb-1"
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus
                />
                <ul className="max-h-[200px] overflow-y-auto">
                  {filteredEntityOptions.map((e) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        className={cn(
                          "w-full text-left px-2 py-1.5 rounded-lg text-[12.5px] hover:bg-surface-2 flex items-center gap-2",
                          filterEntityId === e.id && "bg-accent/10 text-accent",
                        )}
                        onClick={() => { setFilterEntityId(e.id); setEntityPickerOpen(false); setEntitySearch(""); }}
                      >
                        <span aria-hidden>{e.country?.flag ?? "🌐"}</span>
                        <span className="truncate">{e.name}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Field filter chip — shown when filterField is active (deep-linked from FieldEditHistory) */}
          {filterField && (
            <div className="relative">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 h-8 rounded-full border px-3 text-[12.5px] font-medium",
                  "bg-accent/10 border-accent/30 text-accent",
                )}
              >
                {getFieldLabel(filterField) || filterField}
              </span>
              <button
                type="button"
                onClick={() => {
                  setFilterField("");
                  const next = new URLSearchParams(searchParams);
                  next.delete("field");
                  setSearchParams(next, { replace: true });
                }}
                aria-label={t("audit:filter_remove_field", { defaultValue: "Remove field filter" })}
                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/80"
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            </div>
          )}

          {/* Action filter chip */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setActionPickerOpen((v) => !v)}
              className={cn(
                "inline-flex items-center gap-1.5 h-8 rounded-full border px-3 text-[12.5px] font-medium transition-colors",
                filterAction ? "bg-accent/10 border-accent/30 text-accent" : "bg-surface-2 border-border text-muted hover:bg-surface",
              )}
            >
              {filterAction
                ? t(`audit:action_${filterAction}`, { defaultValue: filterAction })
                : t("audit:filter_action", { defaultValue: "Action" })}
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
            {filterAction && (
              <button
                type="button"
                onClick={() => setFilterAction("")}
                aria-label={t("audit:filter_remove_action")}
                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/80"
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            )}
            {actionPickerOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 w-[180px] bg-surface border border-border rounded-[10px] shadow-pop p-1.5">
                <button
                  type="button"
                  className={cn("w-full text-left px-3 py-1.5 rounded-lg text-[12.5px] hover:bg-surface-2", !filterAction && "bg-accent/10 text-accent")}
                  onClick={() => { setFilterAction(""); setActionPickerOpen(false); }}
                >
                  {t("common:all", { defaultValue: "All" })}
                </button>
                {ACTION_TYPES.map((at) => (
                  <button
                    key={at}
                    type="button"
                    className={cn(
                      "w-full text-left px-3 py-1.5 rounded-lg text-[12.5px] hover:bg-surface-2",
                      filterAction === at && "bg-accent/10 text-accent",
                    )}
                    onClick={() => { setFilterAction(at); setActionPickerOpen(false); }}
                  >
                    {t(`audit:action_${at}`, { defaultValue: at })}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date filter chip */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setDatepickerOpen((v) => !v)}
              className={cn(
                "inline-flex items-center gap-1.5 h-8 rounded-full border px-3 text-[12.5px] font-medium transition-colors",
                (filterFromDate || filterToDate) ? "bg-accent/10 border-accent/30 text-accent" : "bg-surface-2 border-border text-muted hover:bg-surface",
              )}
            >
              {(filterFromDate || filterToDate)
                ? `${filterFromDate || "…"} → ${filterToDate || "…"}`
                : t("audit:filter_date", { defaultValue: "Date" })}
              <ChevronDown className="h-3 w-3" aria-hidden />
            </button>
            {(filterFromDate || filterToDate) && (
              <button
                type="button"
                onClick={() => { setFilterFromDate(""); setFilterToDate(""); }}
                aria-label={t("audit:filter_remove_date")}
                className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/80"
              >
                <X className="h-2.5 w-2.5" aria-hidden />
              </button>
            )}
            {datepickerOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 w-[260px] bg-surface border border-border rounded-[10px] shadow-pop p-3 space-y-2">
                {/* Presets */}
                <div className="flex flex-wrap gap-1.5">
                  {[
                    { label: t("audit:date_today", { defaultValue: "Today" }), days: 0 },
                    { label: t("audit:date_7d", { defaultValue: "Last 7 days" }), days: 7 },
                    { label: t("audit:date_30d", { defaultValue: "Last 30 days" }), days: 30 },
                  ].map(({ label, days }) => {
                    const preset = days === 0
                      ? { from: new Date().toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) }
                      : datePreset(days);
                    return (
                      <button
                        key={label}
                        type="button"
                        className="rounded-full px-2.5 py-0.5 text-[11.5px] bg-surface-2 border border-border hover:bg-accent hover:text-white hover:border-accent transition-colors"
                        onClick={() => { setFilterFromDate(preset.from); setFilterToDate(preset.to); }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* Custom */}
                <div className="space-y-1.5">
                  <label htmlFor="history-date-from" className="text-[11px] text-dim font-medium">{t("audit:filter_from_label")}</label>
                  <input
                    id="history-date-from"
                    type="date"
                    value={filterFromDate}
                    onChange={(e) => setFilterFromDate(e.target.value)}
                    className="w-full h-8 px-2 rounded-lg border border-border bg-surface-2 text-base focus:outline-none focus:border-accent"
                  />
                  <label htmlFor="history-date-to" className="text-[11px] text-dim font-medium">{t("audit:filter_to_label")}</label>
                  <input
                    id="history-date-to"
                    type="date"
                    value={filterToDate}
                    onChange={(e) => setFilterToDate(e.target.value)}
                    className="w-full h-8 px-2 rounded-lg border border-border bg-surface-2 text-base focus:outline-none focus:border-accent"
                  />
                </div>
                <button
                  type="button"
                  className="w-full text-[12.5px] font-medium text-accent hover:underline"
                  onClick={() => setDatepickerOpen(false)}
                >
                  {t("common:apply", { defaultValue: "Apply" })}
                </button>
              </div>
            )}
          </div>

          {/* Clear all */}
          {hasActiveFilter && (
            <button
              type="button"
              onClick={clearAllFilters}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[12.5px] text-dim hover:text-red-700 transition-colors"
            >
              <X className="h-3 w-3" aria-hidden />
              {t("common:clear_filters")}
            </button>
          )}
        </div>
      </div>

      {/* Close popovers on outside click */}
      {(entityPickerOpen || actionPickerOpen || datepickerOpen) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setEntityPickerOpen(false); setActionPickerOpen(false); setDatepickerOpen(false); }}
          aria-hidden
        />
      )}

      {/* Quick filter pills */}
      {!isLoading && !groupByEntity && (
        <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-border-2">
          {([
            { key: "", label: t("audit:quick_filter_all", { defaultValue: "All" }), count: quickFilterCounts.all },
            { key: "updated", label: t("audit:quick_filter_updates", { defaultValue: "Updates" }), count: quickFilterCounts.updated },
            { key: "created", label: t("audit:quick_filter_additions", { defaultValue: "Additions" }), count: quickFilterCounts.created },
            { key: "deleted", label: t("audit:quick_filter_removals", { defaultValue: "Removals" }), count: quickFilterCounts.deleted },
            { key: "renewed", label: t("audit:quick_filter_renewals", { defaultValue: "Renewals" }), count: quickFilterCounts.renewed },
          ] as Array<{ key: ActionType | ""; label: string; count: number }>).map(({ key, label, count }) => {
            const isActive = key === "" ? filterAction === "" : filterAction === key;
            return (
              <button
                key={key || "all"}
                type="button"
                onClick={() => setFilterAction(key as ActionType | "")}
                className={cn(
                  "inline-flex items-center gap-1.5 px-2.5 py-1 text-[12.5px] font-medium rounded-full border transition-all",
                  isActive
                    ? "bg-text text-white border-text"
                    : "bg-surface border-border text-muted hover:bg-surface-2 hover:text-text",
                )}
                aria-pressed={isActive}
              >
                {label}
                <span
                  className={cn(
                    "px-1.5 py-px text-[11px] rounded-full font-semibold tabular-nums",
                    isActive ? "bg-white/20 text-white" : "bg-surface-2 text-muted",
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Timeline card */}
      <SectionCard
        title={t("audit:page_title")}
        bodyClassName="p-0"
      >
        {isLoading ? (
          <div className="p-[15px] space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="skeleton h-12" />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <p className="text-[15px] font-medium text-text mt-3">
              {t("audit:empty_no_changes_title", { defaultValue: "No changes recorded yet" })}
            </p>
            <p className="text-[13px] text-muted mt-1.5 max-w-[320px]">
              {t("audit:empty_no_changes_desc", { defaultValue: "Edits made by legal advisors or admin will appear here." })}
            </p>
          </div>
        ) : isNoMatch ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <SearchIcon className="w-8 h-8 text-dim" aria-hidden />
            <p className="text-[15px] font-medium text-text mt-3">
              {t("audit:empty_filtered_title", { defaultValue: "No changes match these filters" })}
            </p>
            <button
              type="button"
              onClick={clearAllFilters}
              className="mt-3 text-[12.5px] font-medium text-accent hover:underline"
            >
              {t("audit:empty_clear_filters", { defaultValue: "Clear filters" })}
            </button>
          </div>
        ) : groupedByEntity ? (
          <HistoryTimelineErrorBoundary>
            <div className="divide-y divide-border-2">
              {groupedByEntity.map(([entityName, entityItems]) => (
                <div key={entityName}>
                  <div className="flex items-center gap-3 py-2 px-[15px] sticky top-0 bg-surface z-[1]" role="separator" aria-hidden>
                    <span className="text-[11px] font-semibold uppercase tracking-[.05em] text-dim whitespace-nowrap">
                      {entityName}
                    </span>
                    <div className="flex-1 h-px bg-border-2" />
                    <span className="text-[10.5px] text-dim tabular-nums">{entityItems.length}</span>
                  </div>
                  <ChangelogTimeline changes={entityItems} showEntityName={false} />
                </div>
              ))}
            </div>
          </HistoryTimelineErrorBoundary>
        ) : (
          <HistoryTimelineErrorBoundary>
            {visibleGroups.map((group) => (
              <div key={group.dateKey}>
                <div
                  className="sticky top-0 bg-surface z-[1] pt-3.5 pb-2 px-6 border-b border-border-2"
                  role="separator"
                  aria-hidden
                >
                  <time
                    dateTime={group.dateKey}
                    className="font-display text-[15.5px] font-medium text-text"
                  >
                    {group.label}
                  </time>
                  <span className="text-dim text-[12.5px] tabular-nums ml-1.5">
                    {t("audit:day_count_changes", {
                      count: group.items.length,
                      defaultValue: `· ${group.items.length} changes`,
                    })}
                  </span>
                </div>
                <ChangelogTimeline changes={group.items} showEntityName />
              </div>
            ))}
          </HistoryTimelineErrorBoundary>
        )}
      </SectionCard>

      {(hasMore || total > 0) && (
        <div className="flex flex-col items-center gap-2 mt-4">
          <p className="text-[12px] text-dim tabular-nums">
            {t("audit:showing_x_of_y", {
              shown: rawItems.length,
              total,
              defaultValue: `Showing ${rawItems.length} of ${total}`,
            })}
          </p>
          {hasMore && (
            <Button
              variant="secondary"
              onClick={() => setLimit((l) => l + 50)}
              disabled={isFetching}
            >
              {isFetching
                ? t("common:loading", { defaultValue: "Loading…" })
                : t("audit:load_more")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
